import React from "react";
import {graphql} from "react-apollo";
import {flowRight as compose} from "lodash";
import {withRouter} from "react-router";
import {formValueSelector} from "redux-form/immutable";
import gql from "graphql-tag";
import {connect} from "react-redux";
import {fromJS, List, Map} from "immutable";
import Form, {FORM_NAME} from "./ClientDispatchForm";
import {
  cancelCreateClientDispatch,
  cancelEditClientDispatch,
  successEditClientDispatch,
} from "../../../actions/ClientDispatchApiActions";
import {LIST_PARTNERS} from "../../../queries/partners";
import {GET_CLIENT_DISPATCH} from "../../../queries/clientDispatches";
import {getAllWarehouses, isStordWarehouse} from "../../../lib/Warehouses";
import {validatePresence} from "../../../lib/Validators";
import {createValidationErrors, createErrors} from "../../../lib/ErrorFormatter";
import Loading from "../../../components/Loading";
import GraphQLErrors from "../../../components/GraphQLErrors";

const FIELDS = ["partnerId", "addressId", "dispatchDate", "reference", "carrierReference", "stordOrderNumber", "notes"];

// Creates a client dispatch, or edits one when given `clientDispatchId`.
class ClientDispatchFormContainer extends React.Component {

  constructor(props) {
    super(props);
    this.state = { errors: [] }
  }

  isEdit() {
    return !!this.props.clientDispatchId;
  }

  validate(values) {
    let errors = Map();
    if (!this.isEdit()) errors = validatePresence(errors, values, "originWarehouseId");
    errors = validatePresence(errors, values, "partnerId");
    return errors.toJS();
  }

  handleSubmit(values) {
    let data = Map(FIELDS.map((field) => [field, values.get(field) || ""]));
    const request = this.isEdit()
      ? this.props.updateClientDispatch(data.set("clientDispatchId", this.props.clientDispatchId).toJS())
      : this.props.createClientDispatch(data.set("originWarehouseId", values.get("originWarehouseId")).toJS());

    return request
      .then((response) => {
        const result = this.isEdit() ? response.data.updateClientDispatch : response.data.createClientDispatch;
        if (result.errors.length > 0) {
          this.setState({errors: createValidationErrors(result.errors)});
        } else if (this.isEdit()) {
          this.props.successEditClientDispatch();
        } else {
          this.props.cancelCreateClientDispatch();
          this.props.router.push(`/client-dispatches/${result.id}`);
        }
      })
      .catch((err) => {
        this.setState({errors: createErrors(err)});
      });
  }

  handleCancel() {
    if (this.isEdit()) {
      this.props.cancelEditClientDispatch();
    } else {
      this.props.cancelCreateClientDispatch();
    }
  }

  initialValues() {
    const dispatch = this.props.dispatchData && this.props.dispatchData.clientDispatch;
    if (!dispatch) return fromJS({originWarehouseId: "", partnerId: "", addressId: ""});
    return fromJS({
      partnerId: dispatch.partnerId,
      addressId: (dispatch.deliveryAddress && dispatch.deliveryAddress.addressId) || "",
      dispatchDate: dispatch.dispatchDate,
      reference: dispatch.reference,
      carrierReference: dispatch.carrierReference,
      stordOrderNumber: dispatch.stordOrderNumber,
      notes: dispatch.notes,
    });
  }

  render() {
    const {partnersData, dispatchData, selectedPartnerId, selectedOriginId} = this.props;
    if (partnersData.loading || (dispatchData && dispatchData.loading)) return <Loading />
    if (partnersData.error) return <GraphQLErrors error={partnersData.error} />

    const clients = fromJS(partnersData.partners || []);
    const partner = clients.find((client) => client.get("partnerId") === selectedPartnerId);
    const dispatch = dispatchData && dispatchData.clientDispatch;
    const origin = this.isEdit() ? (dispatch && dispatch.originWarehouseId) : selectedOriginId;

    return (
      <Form
        initialValues={this.initialValues()}
        warehouses={getAllWarehouses()}
        clients={clients}
        addresses={partner ? partner.get("addresses", List()) : List()}
        showOrigin={!this.isEdit()}
        showStordOrder={isStordWarehouse(origin)}
        submitLabel={this.isEdit() ? "Save" : "Create"}
        errors={this.state.errors}
        validate={this.validate.bind(this)}
        onSubmit={this.handleSubmit.bind(this)}
        onCancel={this.handleCancel.bind(this)}
        />
    )
  }
}

const CREATE_CLIENT_DISPATCH = gql`
  mutation createClientDispatch($data: CreateClientDispatchInput!) {
    createClientDispatch(input: $data) {
      id
      errors { key message }
    }
  }
`;

const UPDATE_CLIENT_DISPATCH = gql`
  mutation updateClientDispatch($data: UpdateClientDispatchInput!) {
    updateClientDispatch(input: $data) {
      errors { key message }
    }
  }
`;

const withData = compose(
  graphql(LIST_PARTNERS, {
    name: "partnersData",
    options: () => ({fetchPolicy: "network-only", variables: {role: "client", active: true}})
  }),
  graphql(GET_CLIENT_DISPATCH, {
    name: "dispatchData",
    skip: (props) => !props.clientDispatchId,
    options: (props) => ({fetchPolicy: "network-only", variables: {id: props.clientDispatchId}})
  }),
  graphql(CREATE_CLIENT_DISPATCH, {
    props: ({mutate}) => ({createClientDispatch: (data) => mutate({variables: {data}})})
  }),
  graphql(UPDATE_CLIENT_DISPATCH, {
    props: ({mutate}) => ({updateClientDispatch: (data) => mutate({variables: {data}})})
  }),
);

const selector = formValueSelector(FORM_NAME);
const mapStateToProps = (state) => ({
  selectedPartnerId: selector(state, "partnerId"),
  selectedOriginId: selector(state, "originWarehouseId"),
});

export default connect(mapStateToProps, {
  cancelCreateClientDispatch,
  cancelEditClientDispatch,
  successEditClientDispatch,
})(withRouter(withData(ClientDispatchFormContainer)));
